import type { ReactNode } from 'react';
import { openableURL } from '@/features/message-frame/frameDocument';

/**
 * The assistant's answer about an email, rendered from a deliberately tiny
 * Markdown subset (ADR 0074): "## " headings, "- " list items, **bold**, and
 * paragraphs. Everything is a React text node or an element React builds —
 * nothing is parsed as HTML, so a "<script>" in an answer is the eight
 * characters it looks like.
 *
 * Links: a web address in the answer becomes a link ONLY when it is one of
 * `links`, the addresses the server found in the email itself. The model can
 * repeat a URL the email contains; a URL it invented, or "corrected", stays
 * plain text and cannot be clicked. `[text](url)` is not a link form here at
 * all.
 */

const URL_RE = /https?:\/\/[^\s<>"'()[\]{}`]+/g;

function linkify(text: string, links: ReadonlySet<string>, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let at = 0;
  let n = 0;
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index;
    let url = match[0];
    const trimmed = url.replace(/[.,;:!?*]+$/, '');
    url = trimmed;
    if (start > at) out.push(text.slice(at, start));
    const parsed = links.has(url) ? openableURL(url) : null;
    if (parsed !== null) {
      out.push(
        <a key={`${keyBase}-l${n++}`} href={parsed.href} target="_blank" rel="noopener noreferrer nofollow">
          {url}
        </a>,
      );
    } else {
      out.push(url);
    }
    at = start + url.length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

/** **bold** spans, then links inside each piece. */
function inline(text: string, links: ReadonlySet<string>, keyBase: string): ReactNode[] {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.flatMap((part, index) =>
    index % 2 === 1 ? (
      <strong key={`${keyBase}-b${index}`}>{linkify(part, links, `${keyBase}-b${index}`)}</strong>
    ) : (
      linkify(part, links, `${keyBase}-t${index}`)
    ),
  );
}

export function AiAnswerText({ text, links }: { text: string; links: readonly string[] }) {
  const allowed = new Set(links);
  const blocks: ReactNode[] = [];
  let items: ReactNode[] = [];
  let paragraph: string[] = [];
  const flushList = () => {
    if (items.length > 0) {
      blocks.push(<ul key={`ul${blocks.length}`}>{items}</ul>);
      items = [];
    }
  };
  const flushParagraph = () => {
    if (paragraph.length > 0) {
      const key = `p${blocks.length}`;
      blocks.push(<p key={key}>{inline(paragraph.join(' '), allowed, key)}</p>);
      paragraph = [];
    }
  };
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    const item = /^(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line);
    if (line === '') {
      flushParagraph();
      flushList();
    } else if (heading) {
      flushParagraph();
      flushList();
      const key = `h${blocks.length}`;
      blocks.push(
        <h3 key={key} className="ai-answer__heading">
          {inline(heading[1]!.replace(/\*\*/g, ''), allowed, key)}
        </h3>,
      );
    } else if (item) {
      flushParagraph();
      const key = `li${blocks.length}-${items.length}`;
      items.push(<li key={key}>{inline(item[1]!, allowed, key)}</li>);
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return <div className="ai-answer">{blocks}</div>;
}
